import { Fragment } from 'react';
import dynamic from 'next/dynamic';

import { useGetUserInsuranceByDoctorId } from '@api/profile/index.rq';
import ImageOption from '@com/shared/cards/imageOption';
import { InsuranceTypeEnum } from '@models/profile';
import type CarouselComponent from '@com/shared/carousel';
import Divider from '@com/_atoms/Divider';
import useInView from '@hooks/useInView';

const Carousel = dynamic(
  () => import('@com/shared/carousel'),
) as typeof CarouselComponent;

type InsuranceProps = {
  recId: number;
};

const Insurances = ({ recId }: InsuranceProps) => {
  const { ref: insurancesRef, isInView: insurancesIsInView } = useInView();
  const { data: basicInsurancesData, isLoading: basicInsurancesIsLoading } =
    useGetUserInsuranceByDoctorId(
      {
        id: recId,
        pageNumber: 1,
        pageSize: 50,
        insuranceType: InsuranceTypeEnum.Basic,
      },
      { enabled: !!recId && !!insurancesIsInView },
    );

  const {
    data: supplementaryInsurancesData,
    isLoading: supplementaryInsurancesIsLoading,
  } = useGetUserInsuranceByDoctorId(
    {
      id: recId,
      pageNumber: 1,
      pageSize: 50,
      insuranceType: InsuranceTypeEnum.Supplementary,
    },
    { enabled: !!recId && !!insurancesIsInView },
  );

  const renderContent = () => {
    if (
      !basicInsurancesIsLoading &&
      !supplementaryInsurancesIsLoading &&
      !basicInsurancesData?.queryResult?.length &&
      !supplementaryInsurancesData?.queryResult?.length
    ) {
      return null;
    }

    return (
      <Fragment>
        <Divider padding={0} className="h-2 bg-surface-secondary mb-3" />

        <div className="flex flex-col pb-2 gap-y-2">
          {!!basicInsurancesData?.queryResult?.length && (
            <Carousel
              title={'بیمه های پایه'}
              items={basicInsurancesData?.queryResult}
              ItemComponent={({ item }) => (
                <ImageOption
                  key={item.id}
                  width={40}
                  height={40}
                  isCircle
                  label={item.insuranceName}
                  image={item.imageUrl}
                  labelClassname="!text-Label/2xSmall line-clamp-2 break-words !text-wrap max-w-[48px] mt-1 h-auto"
                  rootClassName="!justify-start"
                  imageClassName="p-0.5"
                />
              )}
              isLoading={basicInsurancesIsLoading}
              scrollSliderClassName="gap-x-3"
              shimmerCount={5}
              ShimmerComponent={
                <div className="flex flex-col gap-y-1 py-2">
                  <div className="w-20 h-20 bg-surface-secondary rounded animate-pulse" />
                  <div className="w-18 h-3 bg-surface-secondary rounded animate-pulse" />
                </div>
              }
              headerClassName="!h-12"
              containerClassName="!h-[130px]"
            />
          )}

          {!!basicInsurancesData?.queryResult?.length &&
            !!supplementaryInsurancesData?.queryResult?.length && (
              <Divider className="h-[1px] bg-surface-secondary" />
            )}

          {!!supplementaryInsurancesData?.queryResult?.length && (
            <Carousel
              title={'بیمه های تکمیلی'}
              items={supplementaryInsurancesData?.queryResult}
              ItemComponent={({ item }) => (
                <ImageOption
                  key={item.id}
                  label={item.insuranceName}
                  image={item.imageUrl}
                  width={40}
                  height={40}
                  isCircle
                  labelClassname="!text-Label/2xSmall line-clamp-2 break-words !text-wrap max-w-[48px] mt-1 h-auto"
                  rootClassName="!justify-start"
                  imageClassName="p-0.5"
                />
              )}
              isLoading={supplementaryInsurancesIsLoading}
              scrollSliderClassName="gap-x-3"
              shimmerCount={5}
              ShimmerComponent={
                <div className="flex flex-col gap-y-1 py-2">
                  <div className="w-20 h-20 bg-surface-secondary rounded animate-pulse" />
                  <div className="w-18 h-3 bg-surface-secondary rounded animate-pulse" />
                </div>
              }
              headerClassName="!h-12"
              containerClassName="!h-[130px]"
            />
          )}
        </div>
      </Fragment>
    );
  };

  return <div ref={insurancesRef}>{renderContent()}</div>;
};

export default Insurances;
